import { useCallback, useEffect, useState } from 'react'
import {
  View, Text, Modal, TouchableOpacity, StyleSheet,
  ScrollView, ActivityIndicator
} from 'react-native'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import Ionicons from '@expo/vector-icons/Ionicons'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { supabase } from '../lib/supabase'
import { useThemeColors } from '../lib/useThemeColors'
import { fonts } from '../lib/theme'

const TOTAL = 10

function shuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function resultMessage(score, t) {
  const pct = score / TOTAL
  if (pct >= 0.9) return t('quiz.result_excellent')
  if (pct >= 0.7) return t('quiz.result_good')
  if (pct >= 0.5) return t('quiz.result_average')
  return t('quiz.result_poor')
}

export default function QuizModal({ visible, onClose, raceCode = 'VUELTA', raceLabel = 'Vuelta a España' }) {
  const tc = useThemeColors()
  const { t } = useTranslation()
  const insets = useSafeAreaInsets()

  const [questions, setQuestions] = useState([])
  const [loading, setLoading]     = useState(true)
  const [current, setCurrent]     = useState(0)
  const [score, setScore]         = useState(0)
  const [selected, setSelected]   = useState(null)
  const [done, setDone]           = useState(false)

  const lang = i18n.language?.substring(0, 2) ?? 'it'
  const supportedLang = ['it', 'en', 'es'].includes(lang) ? lang : 'it'

  const loadQuestions = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await supabase.rpc('fn_get_quiz_questions', {
        p_race_code: raceCode,
        p_lang: supportedLang,
        p_limit: TOTAL,
      })
      if (data?.length) {
        // Shuffle risposte mantenendo il correct_index aggiornato
        const prepared = data.map(q => {
          const answers = q.answers ?? []
          const pairs = answers.map((text, i) => ({ text, correct: i === q.correct_index }))
          const shuffled = shuffle(pairs)
          return {
            ...q,
            answers: shuffled.map(p => p.text),
            correct_index: shuffled.findIndex(p => p.correct),
          }
        })
        setQuestions(prepared)
      }
    } catch (e) {
      console.error('[QuizModal]', e)
    } finally {
      setLoading(false)
    }
  }, [raceCode, supportedLang])

  useEffect(() => {
    if (visible) loadQuestions()
  }, [visible, loadQuestions])

  const q = questions[current]
  const answered = selected !== null

  const handleAnswer = useCallback((idx) => {
    if (answered) return
    setSelected(idx)
    if (idx === q?.correct_index) setScore(s => s + 1)
  }, [answered, q])

  const handleNext = useCallback(() => {
    if (current + 1 >= questions.length) {
      setDone(true)
    } else {
      setCurrent(c => c + 1)
      setSelected(null)
    }
  }, [current, questions.length])

  const handleRestart = useCallback(() => {
    loadQuestions()
    setCurrent(0)
    setScore(0)
    setSelected(null)
    setDone(false)
  }, [loadQuestions])

  const handleClose = useCallback(() => {
    onClose()
    setTimeout(() => {
      setCurrent(0)
      setScore(0)
      setSelected(null)
      setDone(false)
      setQuestions([])
    }, 400)
  }, [onClose])

  const getAnswerStyle = (idx) => {
    if (!answered) return [styles.answerBtn, { borderColor: tc.border, backgroundColor: tc.bgCard2 }]
    if (idx === q.correct_index) return [styles.answerBtn, { borderColor: '#86efac', backgroundColor: '#dcfce7' }]
    if (idx === selected) return [styles.answerBtn, { borderColor: '#fca5a5', backgroundColor: '#fee2e2' }]
    return [styles.answerBtn, { borderColor: tc.border, backgroundColor: tc.bgCard2, opacity: 0.5 }]
  }

  const getAnswerTextColor = (idx) => {
    if (!answered) return tc.text
    if (idx === q.correct_index) return '#166534'
    if (idx === selected) return '#991b1b'
    return tc.textDim
  }

  const difficultyColor = (d) => d === 1 ? '#10B981' : d === 2 ? '#F59E0B' : tc.rosa
  const difficultyLabel = (d) => d === 1 ? t('quiz.easy') : d === 2 ? t('quiz.medium') : t('quiz.hard')

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={handleClose}>
      <View style={[styles.overlay, { paddingBottom: insets.bottom }]}>
        <View style={[styles.sheet, { backgroundColor: tc.bgCard }]}>

          {/* Header */}
          <View style={[styles.header, { borderBottomColor: tc.border }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <MaterialCommunityIcons name="bike" size={18} color={tc.rosaText} />
              <Text style={[styles.title, { color: tc.text }]}>Quiz — {raceLabel}</Text>
            </View>
            <TouchableOpacity onPress={handleClose} style={[styles.closeBtn, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
              <Ionicons name="close" size={16} color={tc.textMuted} />
            </TouchableOpacity>
          </View>

          {loading ? (
            <View style={styles.loadingWrap}>
              <ActivityIndicator color={tc.rosaText} size="large" />
              <Text style={[styles.loadingText, { color: tc.textMuted }]}>{t('quiz.loading')}</Text>
            </View>
          ) : questions.length === 0 ? (
            <View style={styles.loadingWrap}>
              <MaterialCommunityIcons name="alert-circle-outline" size={36} color={tc.textMuted} />
              <Text style={[styles.loadingText, { color: tc.textMuted }]}>{t('quiz.no_questions')}</Text>
            </View>
          ) : done ? (
            /* ── Risultato ── */
            <View style={styles.resultWrap}>
              <MaterialCommunityIcons
                name={score >= 7 ? 'trophy' : score >= 5 ? 'bike' : 'emoticon-neutral-outline'}
                size={52}
                color={score >= 7 ? '#f59e0b' : tc.rosaText}
              />
              <Text style={[styles.resultScore, { color: tc.text }]}>{score}/{questions.length}</Text>
              <Text style={[styles.resultPct, { color: tc.rosaText }]}>
                {Math.round((score / questions.length) * 100)}%
              </Text>
              <Text style={[styles.resultMsg, { color: tc.textMuted }]}>
                {resultMessage(score, t)}
              </Text>
              <TouchableOpacity
                style={[styles.restartBtn, { backgroundColor: tc.rosa }]}
                onPress={handleRestart}
              >
                <Text style={[styles.restartBtnText, { color: tc.brandText }]}>{t('quiz.restart')}</Text>
              </TouchableOpacity>
            </View>
          ) : (
            /* ── Domanda ── */
            <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
              {/* Progress */}
              <View style={[styles.progressBar, { backgroundColor: tc.border }]}>
                <View style={[styles.progressFill, { backgroundColor: tc.rosa, width: `${(current / questions.length) * 100}%` }]} />
              </View>

              <View style={styles.counterRow}>
                <Text style={[styles.counter, { color: tc.textDim }]}>
                  {t('quiz.question_count', { current: current + 1, total: questions.length })}
                </Text>
                <Text style={[styles.scoreCounter, { color: tc.rosaText }]}>
                  {score} pt
                </Text>
              </View>

              {/* Badge difficoltà */}
              {q?.difficulty && (
                <View style={[styles.badge, { backgroundColor: difficultyColor(q.difficulty) + '20', borderColor: difficultyColor(q.difficulty) + '40', borderWidth: 1 }]}>
                  <Text style={[styles.badgeText, { color: difficultyColor(q.difficulty) }]}>
                    {difficultyLabel(q.difficulty)}
                  </Text>
                </View>
              )}

              {/* Domanda */}
              <Text style={[styles.question, { color: tc.text }]}>{q?.question}</Text>

              {/* Risposte */}
              <View style={styles.answers}>
                {(q?.answers ?? []).map((ans, idx) => (
                  <TouchableOpacity
                    key={idx}
                    style={getAnswerStyle(idx)}
                    onPress={() => handleAnswer(idx)}
                    disabled={answered}
                    activeOpacity={0.7}
                  >
                    <Text style={[styles.answerText, { color: getAnswerTextColor(idx) }]}>
                      {ans}
                    </Text>
                    {answered && idx === q.correct_index && (
                      <MaterialCommunityIcons name="check-circle" size={16} color="#166534" />
                    )}
                    {answered && idx === selected && idx !== q.correct_index && (
                      <MaterialCommunityIcons name="close-circle" size={16} color="#991b1b" />
                    )}
                  </TouchableOpacity>
                ))}
              </View>

              {/* Feedback + Next */}
              {answered && (
                <View style={styles.feedbackWrap}>
                  <Text style={[styles.feedback, { color: selected === q.correct_index ? '#166534' : '#991b1b' }]}>
                    {selected === q.correct_index
                      ? t('quiz.correct')
                      : t('quiz.wrong', { answer: q.answers[q.correct_index] })}
                  </Text>
                  <TouchableOpacity
                    style={[styles.nextBtn, { backgroundColor: tc.rosa }]}
                    onPress={handleNext}
                  >
                    <Text style={[styles.nextBtnText, { color: tc.brandText }]}>
                      {current + 1 < questions.length ? t('quiz.next') : t('quiz.see_result')}
                    </Text>
                    <MaterialCommunityIcons name="arrow-right" size={16} color={tc.brandText} />
                  </TouchableOpacity>
                </View>
              )}
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  )
}

const styles = StyleSheet.create({
  overlay:      { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet:        { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '92%' },
  header:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1 },
  title:        { fontFamily: fonts.condensedBold, fontSize: 16, textTransform: 'uppercase' },
  closeBtn:     { width: 32, height: 32, borderRadius: 8, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  loadingWrap:  { padding: 48, alignItems: 'center', gap: 12 },
  loadingText:  { fontSize: 13, textAlign: 'center' },
  content:      { padding: 16, paddingBottom: 32 },
  progressBar:  { height: 3, borderRadius: 2, marginBottom: 12 },
  progressFill: { height: 3, borderRadius: 2 },
  counterRow:   { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 },
  counter:      { fontSize: 12, fontWeight: '600' },
  scoreCounter: { fontSize: 12, fontWeight: '700' },
  badge:        { alignSelf: 'flex-start', borderRadius: 20, paddingHorizontal: 10, paddingVertical: 4, marginBottom: 12 },
  badgeText:    { fontSize: 11, fontWeight: '700' },
  question:     { fontSize: 16, fontWeight: '600', lineHeight: 24, marginBottom: 20 },
  answers:      { gap: 10 },
  answerBtn:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 14, borderRadius: 12, borderWidth: 1 },
  answerText:   { fontSize: 14, fontWeight: '500', flex: 1 },
  feedbackWrap: { marginTop: 16, gap: 12 },
  feedback:     { fontSize: 13, fontWeight: '600', lineHeight: 18 },
  nextBtn:      { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 12, padding: 14 },
  nextBtnText:  { fontFamily: fonts.condensedBold, fontSize: 15 },
  resultWrap:   { padding: 32, alignItems: 'center', gap: 12 },
  resultScore:  { fontFamily: fonts.condensedBold, fontSize: 48 },
  resultPct:    { fontFamily: fonts.condensedBold, fontSize: 20 },
  resultMsg:    { fontSize: 14, textAlign: 'center', lineHeight: 20 },
  restartBtn:   { borderRadius: 12, paddingHorizontal: 32, paddingVertical: 14, marginTop: 8 },
  restartBtnText: { fontFamily: fonts.condensedBold, fontSize: 15 },
})